import React from 'react';
import { fetchProductSearch, ProductSearchResponse } from '../services/catalog-service';

interface UseProductSearchArgs {
  query: string;
  limit?: number;
  offset?: number;
  includeFacets?: boolean;
  category?: string | null;
  aroma?: string | number | null;
  enabled?: boolean;
}

const initialFacets = { categories: [], aromas: [] };
const initialState: ProductSearchResponse = {
  count: 0,
  next: null,
  previous: null,
  results: [],
  facets: initialFacets
};

export const useProductSearch = ({
  query,
  limit = 10,
  offset = 0,
  includeFacets = false,
  category = null,
  aroma = null,
  enabled = true
}: UseProductSearchArgs) => {
  const [data, setData] = React.useState<ProductSearchResponse>(initialState);
  const [isLoading, setIsLoading] = React.useState(false);
  const [error, setError] = React.useState<Error | null>(null);
  const trimmed = (query || '').trim();
  const canSearch = enabled && trimmed.length > 0;

  React.useEffect(() => {
    let isActive = true;
    const runSearch = async () => {
      if (!canSearch) {
        setData(initialState);
        setIsLoading(false);
        setError(null);
        return;
      }

      setIsLoading(true);
      setError(null);

      try {
        const response = await fetchProductSearch({
          q: trimmed,
          limit,
          offset,
          includeFacets,
          category: category || undefined,
          aroma: aroma ?? undefined
        });
        if (!isActive) return;
        setData({
          ...response,
          facets: {
            categories: response.facets?.categories || [],
            aromas: response.facets?.aromas || []
          }
        });
      } catch (err) {
        if (!isActive) return;
        setError(err instanceof Error ? err : new Error('Не удалось выполнить поиск'));
        setData(initialState);
      } finally {
        if (!isActive) return;
        setIsLoading(false);
      }
    };

    runSearch();

    return () => {
      isActive = false;
    };
  }, [trimmed, limit, offset, includeFacets, category, aroma, canSearch]);

  const refetch = React.useCallback(() => {
    if (!canSearch) return Promise.resolve();
    return fetchProductSearch({
      q: trimmed,
      limit,
      offset,
      includeFacets,
      category: category || undefined,
      aroma: aroma ?? undefined
    }).then((response) => {
      setData({
        ...response,
        facets: {
          categories: response.facets?.categories || [],
          aromas: response.facets?.aromas || []
        }
      });
    });
  }, [canSearch, trimmed, limit, offset, includeFacets, category, aroma]);

  return {
    ...data,
    isLoading,
    error,
    canSearch,
    refetch
  };
};

export default useProductSearch;
